// @flow
import React, { Component } from 'react';
import type { Node } from 'react';
import mergeWith from 'lodash-es/mergeWith';

import * as validationUtils from './validation';
import * as validators from './validation/validators';

// $FlowFixMe
import InnerForm from './InnerForm/InnerForm';

export const mergeFormData = (...args: Array<any>) =>
  mergeWith({}, ...args, (a, b) => {
    if (Array.isArray(b)) {
      return b;
    }
    return undefined;
  });

/**
 * Recursively walk through the data shape.
 * Each leaf in the data tree can either be a validator function,
 * or array of validator functions
 */
export type Validators<D> = $Shape<
  $ObjMap<
    D,
    (<V: Object>(V) => Function | Array<Function> | Validators<V>) &
      (<V>(V) => Function | Array<Function>)
  >
>;

export type RenderPropArgs<D> = {
  data: D,
  onChange: ($DeepShape<D>) => void,
  validationErrors: any,
  onSubmit: () => *,
  submitted: boolean,
  valid: boolean,
  clearData: () => void,
};

export type Props<D: {}> = {
  /**
   * In order for type inference to work properly
   * in the <Form>, the `initialData` prop must be
   * strongly typed (not rely on inference).
   *
   * i.e.
   * // Bad
   * initialData={{ name: props.name }}
   *
   * // Good
   * initialData={({ name: props.name}: InitialData)}
   *
   * // Good
   * initialData={props.initialData} // this works because props themselves are strongly typed
   */
  initialData: D,
  validators?: Validators<D> | (D => Validators<D>),
  onSubmit?: D => *,
  children: (RenderPropArgs<D>) => Node,
  nested?: boolean,
  validateOnChange?: boolean,
  onChange?: ({
    data: D,
    validationErrors: any,
    valid: boolean,
  }) => void,
  onPreSubmit?: (valid: boolean, validationErrors: Validators<D> | (D => Validators<D>)) => *,
};

export type State = {
  initialValidationErrors: any,
  data: ?Object,
  submitted: boolean,
  validationErrors: ?{},
  valid: boolean,
};

export default class Form extends Component<Props<*>, State> {
  static defaultProps = {
    validators: {},
    onSubmit: () => {},
    nested: false,
    validateOnChange: false,
    onChange: () => {},
    onPreSubmit: () => {},
  };

  static validators = validators;

  state = {
    initialValidationErrors: validationUtils.initializeValidation(
      this.props.initialData,
      this.props.validators
    ),
    data: undefined,
    submitted: false,
    validationErrors: undefined,
    valid: true,
  };

  componentWillReceiveProps(nextProps: Props<*>) {
    const { initialData, validators: formValidators } = nextProps;
    const { data, valid } = this.state;
    if (!data && !valid) {
      const { valid: nextValid, errors } = validationUtils.validate(initialData, formValidators);
      this.setState(() => ({ valid: nextValid, validationErrors: errors }));
    }
  }

  handleChange = (updates: {}) => {
    const { validators: formValidators, initialData, validateOnChange, onChange } = this.props;
    const { valid, data, validationErrors } = this.state;
    const nextDirtyData = mergeFormData(data, updates);
    const mergedData = mergeFormData(initialData, nextDirtyData);
    let nextValid = valid;
    let nextErrors = validationErrors;
    let nextState = {};

    // Do validation
    if (validateOnChange || !valid) {
      const validationResults = validationUtils.validate(mergedData, formValidators);
      nextValid = validationResults.valid;
      nextErrors = validationResults.errors;
      nextState = { valid: nextValid, validationErrors: nextErrors };
    }

    nextState = { ...nextState, data: nextDirtyData, submitted: false };

    this.setState(nextState);

    if (onChange) {
      onChange({
        data: mergedData,
        validationErrors: nextErrors,
        valid: nextValid,
      });
    }
  };

  handleSubmit = (e: any) => {
    const { initialData, onSubmit, onPreSubmit } = this.props;
    const { data } = this.state;
    const mergedFormData = mergeFormData(initialData, data);
    const { valid, errors } = validationUtils.validate(mergedFormData, this.props.validators);

    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // validate needs to fire to set the flags correctly no matter it is valid or not
    this.setState(() => ({ valid, validationErrors: errors, submitted: true }));

    if (onPreSubmit) {
      onPreSubmit(valid, errors);
    }

    if (valid && onSubmit) {
      return onSubmit(mergedFormData);
    }
    return valid;
  };

  handleKeyPress = (e: any) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      this.handleSubmit();
    }
  };

  handleClearData = () => {
    this.setState(() => ({
      data: undefined,
      submitted: false,
      validationErrors: undefined,
      valid: true,
    }));
  };

  submit() {
    return this.handleSubmit();
  }

  render() {
    const {
      validators: v,
      initialData,
      children,
      onSubmit,
      nested,
      onChange,
      ...other
    } = this.props;
    const { initialValidationErrors, data, submitted, valid, validationErrors } = this.state;
    const { handleSubmit, handleKeyPress } = this;

    return (
      <InnerForm
        nested={nested}
        submitted={submitted}
        handleSubmit={handleSubmit}
        handleKeyPress={handleKeyPress}
        validationErrors={validationErrors}
        initialValidationErrors={initialValidationErrors}
        {...other}
      >
        {children({
          submitted,
          valid,
          data: mergeFormData(initialData, data),
          validationErrors: validationErrors || initialValidationErrors,
          onChange: this.handleChange,
          onSubmit: this.handleSubmit,
          clearData: this.handleClearData,
        })}
      </InnerForm>
    );
  }
}



// WEBPACK FOOTER //
// ../react-lib/src/components/utils/Form/Form.jsx